import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Code, Globe, Users, Zap, Award, Briefcase, BookOpen } from 'lucide-react';
import Button from '../components/atoms/Button';
import Card from '../components/atoms/Card';
import ResponsiveContainer from '../components/molecules/ResponsiveContainer';

const AboutPage: React.FC = () => {
  const navigate = useNavigate();

  const teamMembers = [
    {
      name: 'Glenn Rodney',
      role: 'Founder & CEO',
      bio: 'Blockchain security expert with over 10 years of experience in cryptography and secure systems design.',
      image: 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=600'
    }
  ];

  const values = [
    {
      title: 'Security First',
      description: 'We believe security should never be an afterthought. It\'s built into everything we do.',
      icon: Shield
    },
    {
      title: 'Innovation',
      description: 'Pushing the boundaries of what\'s possible with blockchain and encryption technology.',
      icon: Zap
    },
    {
      title: 'Transparency',
      description: 'Open and honest in our approach to security and privacy.',
      icon: Code
    },
    {
      title: 'User-Centric',
      description: 'Building solutions that prioritize user experience without compromising security.',
      icon: Users
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                icon={ArrowLeft}
                onClick={() => navigate('/')}
                className="text-gray-600 hover:text-gray-900"
              >
                Back to Home
              </Button>
              <div className="h-6 w-px bg-gray-300" />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
                  <Shield className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">About Us</h1>
                  <p className="text-gray-600">Learn about Aegis Vault and our mission</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <ResponsiveContainer maxWidth="full" padding="none">
          <div className="space-y-16">
            {/* Hero Section */}
            <div className="text-center max-w-3xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Securing Your Digital Legacy
                </h2>
                <p className="text-xl text-gray-600 mb-8">
                  Aegis Vault was founded with a simple mission: to provide uncompromising security for your most important documents, powered by blockchain technology and end-to-end encryption.
                </p>
                <div className="flex justify-center">
                  <div className="w-24 h-1 bg-primary-500 rounded-full"></div>
                </div>
              </motion.div>
            </div>

            {/* Our Story */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Story</h3>
                <div className="space-y-4 text-gray-600">
                  <p>
                    Founded in 2023 by Glenn Rodney through GravexLabs, Aegis Vault was born from a recognition that traditional document storage solutions lacked the security and permanence needed for our most critical information.
                  </p>
                  <p>
                    After experiencing firsthand the challenges of managing important documents during a family emergency, Glenn set out to build a solution that would combine military-grade encryption with the immutability of blockchain technology.
                  </p>
                  <p>
                    Today, Aegis Vault stands at the forefront of secure document management, offering individuals and businesses a platform where security never compromises usability.
                  </p>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="rounded-xl overflow-hidden shadow-xl"
              >
                <img 
                  src="https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
                  alt="Team working on security solutions" 
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </div>

            {/* Our Values */}
            <div>
              <div className="text-center mb-12">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Values</h3>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  These core principles guide everything we do at Aegis Vault, from product development to customer support.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {values.map((value, index) => (
                  <motion.div
                    key={value.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 * index }}
                  >
                    <Card className="h-full p-6">
                      <div className="flex flex-col items-center text-center">
                        <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                          <value.icon className="h-8 w-8 text-primary-600" />
                        </div>
                        <h4 className="text-xl font-semibold text-gray-900 mb-2">{value.title}</h4>
                        <p className="text-gray-600">{value.description}</p>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Our Team */}
            <div>
              <div className="text-center mb-12">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Team</h3>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  Meet the people behind Aegis Vault who are passionate about security, privacy, and building exceptional products.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {teamMembers.map((member, index) => (
                  <motion.div
                    key={member.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 * index }}
                  >
                    <Card className="h-full p-6">
                      <div className="flex flex-col items-center text-center">
                        <div className="w-24 h-24 rounded-full overflow-hidden mb-4">
                          <img 
                            src={member.image} 
                            alt={member.name} 
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <h4 className="text-xl font-semibold text-gray-900 mb-1">{member.name}</h4>
                        <p className="text-primary-600 font-medium mb-3">{member.role}</p>
                        <p className="text-gray-600">{member.bio}</p>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Company Info */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <Briefcase className="h-6 w-6 text-primary-600" />
                    <h4 className="text-lg font-semibold text-gray-900">Company</h4>
                  </div>
                  <div className="space-y-2 text-gray-600">
                    <p>GravexLabs</p>
                    <p>Founded in 2023</p>
                    <p>Delaware, USA</p>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <Globe className="h-6 w-6 text-primary-600" />
                    <h4 className="text-lg font-semibold text-gray-900">Locations</h4>
                  </div>
                  <div className="space-y-2 text-gray-600">
                    <p>Headquarters: San Francisco, CA</p>
                    <p>Development: Remote-first</p>
                    <p>Support: Global</p>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <Award className="h-6 w-6 text-primary-600" />
                    <h4 className="text-lg font-semibold text-gray-900">Recognition</h4>
                  </div>
                  <div className="space-y-2 text-gray-600">
                    <p>Web3 Security Innovation Award 2023</p>
                    <p>Top 10 Blockchain Startups to Watch</p>
                    <p>Encryption Excellence Award</p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Join Us on Our Mission</h3>
              <p className="text-gray-600 max-w-2xl mx-auto mb-8">
                We're always looking for talented individuals who share our passion for security, privacy, and building exceptional products.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  variant="primary" 
                  size="lg"
                  onClick={() => navigate('/careers')}
                >
                  View Open Positions
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  onClick={() => navigate('/contact')}
                >
                  Contact Us
                </Button>
              </div>
            </div>
          </div>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default AboutPage;